import { ActionContext } from 'vuex';
import { RootState } from '@/store/store';
import {
  PendingDocumentsState,
  PendingProjectSettings
} from './pending-documents.state';
import { PendingDocumentSettingsDto as PendingDocumentSettings } from '@/jbi-shared/types/full-document-revision-object.types';
import { pendingDocumentsApi } from '@/api/pending-documents.api';
import {
  PendingDocumentSettingsDto,
  PendingProjectSettingsDto
} from '@/jbi-shared/types/full-document-revision-object.types';
import { AxiosResponse } from 'axios';

export interface GetDocumentSettingsPayload {
  documentId: number;
}

export interface UpdateDocumentSettingsPayload {
  documentId: number;
  settings: PendingDocumentSettingsDto;
}

export interface GetProjectSettingsPayload {
  documentId: number;
}

export interface UpdateProjectSettingsPayload
  extends PendingProjectSettingsDto {
  pendingDocumentId: number;
}

export const pendingDocumentsActions = {
  /**
   * Get document settings for a pending document
   */
  async getDocumentSettings(
    { commit }: ActionContext<PendingDocumentsState, RootState>,
    { documentId }: GetDocumentSettingsPayload
  ) {
    try {
      commit('GET_DOCUMENT_SETTINGS_LOADING');

      const {
        data
      }: AxiosResponse<PendingDocumentSettings> = await pendingDocumentsApi.getDocumentSettings(
        documentId
      );

      commit('GET_DOCUMENT_SETTINGS_SUCCESS', {
        documentId,
        settings: data
      });

      return data;
    } catch (error) {
      commit('GET_DOCUMENT_SETTINGS_ERROR', error);
      throw error;
    }
  },

  /**
   * Update document settings for a pending document
   */
  async updateDocumentSettings(
    { commit }: ActionContext<PendingDocumentsState, RootState>,
    { documentId, settings }: UpdateDocumentSettingsPayload
  ) {
    try {
      commit('UPDATE_DOCUMENT_SETTINGS_LOADING');

      await pendingDocumentsApi.updateDocumentSettings(documentId, settings);

      commit('UPDATE_DOCUMENT_SETTINGS_SUCCESS', {
        documentId,
        settings
      });
    } catch (error) {
      commit('UPDATE_DOCUMENT_SETTINGS_ERROR', error);
      throw error;
    }
  },

  /**
   * Get project settings for a pending document
   */
  async getProjectSettings(
    { commit }: ActionContext<PendingDocumentsState, RootState>,
    { documentId }: GetProjectSettingsPayload
  ) {
    try {
      commit('GET_PROJECT_SETTINGS_LOADING');

      const {
        data
      }: AxiosResponse<PendingProjectSettings> = await pendingDocumentsApi.getProjectSettings(
        documentId
      );

      commit('GET_PROJECT_SETTINGS_SUCCESS', {
        documentId,
        settings: data
      });

      return data;
    } catch (error) {
      commit('GET_PROJECT_SETTINGS_ERROR', error);
      throw error;
    }
  },

  /**
   * Update project settings for a pending document
   */
  async updatePendingProjectSettings(
    { commit, rootState }: ActionContext<PendingDocumentsState, RootState>,
    {
      pendingDocumentId,
      projectTitle,
      primaryResearchNodeId,
      researchNodeIds
    }: UpdateProjectSettingsPayload
  ) {
    try {
      commit('UPDATE_PROJECT_SETTINGS_LOADING');

      await pendingDocumentsApi.updateProjectSettings(pendingDocumentId, {
        projectTitle,
        primaryResearchNodeId,
        researchNodeIds
      });

      // Transform IDs to full ResearchNode objects for store consistency
      const activeResearchNodes = rootState.projects.activeResearchNodes || [];
      const primaryResearchNode = activeResearchNodes.find(
        (node) => node.id === primaryResearchNodeId
      );
      // If it does not exist, it will be undefined because of the find method
      const researchNodes = activeResearchNodes.filter((node) =>
        researchNodeIds?.includes(node.id)
      );

      commit('UPDATE_PROJECT_SETTINGS_SUCCESS', {
        documentId: pendingDocumentId,
        settings: {
          projectTitle,
          primaryResearchNode,
          researchNodes
        }
      });
    } catch (error) {
      commit('UPDATE_PROJECT_SETTINGS_ERROR', error);
      throw error;
    }
  },

  /**
   * Clear project settings for a specific document from store
   */
  clearProjectSettings(
    { commit }: ActionContext<PendingDocumentsState, RootState>,
    { documentId }: { documentId: number }
  ) {
    commit('CLEAR_PROJECT_SETTINGS', documentId);
  }
};
